import { type SanityImageSource } from "@sanity/image-url/lib/types/types";
import { groq } from "next-sanity";
import { getClient } from "../sanity";
import { type BlogSimple, blogSimpleFragment } from "./blogs";
import { type PlaybookSimple, playbookSimpleFragment } from "./playbooks";

export type IntegrationSimple = {
  name: string;
  icon: SanityImageSource;
  iconUrl: string;
  slug: string;
  vendorUrl?: string;
};

export const getSimpleIntegrations = async (
  type: "source" | "destination",
  slugs?: string[],
): Promise<IntegrationSimple[]> => {
  const query = groq`
    *[_type == "integration" && type == "${type}" && status == "released" 
      ${slugs ? `&& slug in [${slugs.map((slug) => "'" + slug + "'")}]` : ""}
    ] {
      ${integrationSimpleFragment}
    }
  `;

  const integrations = await getClient().fetch(query);
  return integrations;
};

export const integrationSimpleFragment = "name, slug, vendorUrl, 'iconUrl': icon.asset->url";

export const getSimpleIntegration = async (
  slug: string,
  type?: "source" | "destination",
): Promise<IntegrationSimple> => {
  const query = groq`
    *[_type == "integration" && 
      slug == "${slug}" && status == "released" ${type ? `&& type == "${type}"` : ""}
    ] {
      ${integrationSimpleFragment}
    }
  `;
  const integration = await getClient().fetch(query);
  return integration?.[0] ?? null;
};

export type Integration = {
  name: string;
  slug: string;
  type: string;
  status: string;
  icon: SanityImageSource;
  shortPitch?: string;
  longPitch?: string;
  features?: string[];
  vendorAbout?: string;
  vendorUrl?: string;
  iconUrl: string;
  categories?: Array<{
    name: string;
    shortDescription?: string;
  }>;
  supportsInWarehouseDiffing?: boolean;
  supportedQueryTypes?: string[];
};

const integrationFragment =
  "name, slug, type, status, icon, shortPitch, longPitch, features, vendorAbout, vendorUrl, 'iconUrl': icon.asset->url, 'categories': categories[]->{name, shortDescription}, supportsInWarehouseDiffing, supportedQueryTypes";

export const getIntegration = async (slug: string, type?: "source" | "destination"): Promise<Integration> => {
  const query = groq`
    *[_type == "integration" && 
      slug == "${slug}" &&
      (status == "released" || ((status == "alpha" || status == "coming_soon") && shouldGenerateWaitlistLandingPage == true))
      ${type ? `&& type == "${type}"` : ""}
    ] {
      ${integrationFragment}
    }
  `;
  const integration = await getClient().fetch(query);
  return integration?.[0] ?? null;
};

export type JointIntegration = {
  name: string;
  slug: string;
  jointPitch: string;
  seoSectionTitle?: string;
  seoSectionContent?: string;
  metaTitle?: string;
  metaDescription?: string;
  source: Integration;
  destination: Integration;
  relatedBlogs?: BlogSimple[];
  relatedPlaybooks?: PlaybookSimple[];
};

export const getJointIntegration = async (jointSlug: string): Promise<JointIntegration> => {
  const query = groq`
    *[_type == "jointIntegration" && slug == "${jointSlug}"] {
      name,
      slug,
      jointPitch,
      seoSectionTitle,
      seoSectionContent,
      metaTitle,
      metaDescription,
      'source': source->{
        ${integrationFragment}
      },
      'destination': destination->{
        ${integrationFragment}
      },
      'relatedBlogs': relatedBlogs[]->{
        ${blogSimpleFragment}
      },
      'relatedPlaybooks': relatedPlaybooks[]->{
        ${playbookSimpleFragment}
      },
    }
  `;
  const jointIntegration = await getClient().fetch(query);
  return jointIntegration?.[0] ?? null;
};
